import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';

import { getStatusColor, removeHttpVariations } from '../../../utils/utilFunctions';
import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML } from '../utils';

import StyledWrapper from './style';

import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import { getElementIcon } from '../utils';
import CustomInputLink from './CustomInputLink';
import Tooltip from '@mui/material/Tooltip';

const MUILinkInput = forwardRef((props, ref) => {
	const { name, defaultValue, disabled, isFormBuilder } = props;
	const [hasError, setHasError] = useState(props?.data?.error || false);
	const url = (defaultValue && defaultValue[1]) || '';

	// Internal refs to track actual input values
	const titleInputRef = useRef(null);
	const linkInputRef = useRef(null);
	const [titleValue, setTitleValue] = useState((defaultValue && defaultValue[0]) || '');
	const [linkValue, setLinkValue] = useState((defaultValue && defaultValue[1]) || '');

	useImperativeHandle(
		ref,
		() => ({
			get value() {
				return [titleValue, linkValue];
			},
			getValue: () => [titleValue, linkValue],
			// For compatibility with existing code that accesses .value directly
			titleValue,
			linkValue
		}),
		[titleValue, linkValue]
	);

	function areAllEmptyStrings(array) {
		return array?.every((entry) => entry === '');
	}

	const allEmpty = defaultValue ? areAllEmptyStrings(defaultValue) : true;

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);

	const handleTitleChange = (e) => {
		setHasError(false);
		setTitleValue(e.target.value);
	};

	const handleLinkChange = (e) => {
		setHasError(false);
		setLinkValue(e.target.value);
	};

	const styleForNotFormBuilder = !isFormBuilder
		? { width: '100%', marginTop: '16px' }
		: { width: '100%' };
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [isExternalOverflowing, setIsExternalOverflowing] = useState(false);
	const labelRef = useRef(null);
	const labelExternalRef = useRef(null);

	useEffect(() => {
		setHasError(props?.data?.error || false);
	}, [props?.data?.error]);

	// Update internal values when defaultValue changes (e.g., when loading saved data)
	useEffect(() => {
		if (defaultValue) {
			setTitleValue(defaultValue[0] || '');
			setLinkValue(defaultValue[1] || '');
		}
	}, [defaultValue]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
			if (labelExternalRef.current) {
				setIsExternalOverflowing(
					labelExternalRef.current.scrollWidth > labelExternalRef.current.clientWidth
				);
			}
		}, 100);
	});

	return (
		<>
			{disabled ? (
				<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
					<Tooltip
						arrow
						placement="top"
						title={isOverflowing ? `${props?.data?.label}` : ''}
						enterTouchDelay={0}
					>
						<StyledWrapper.CustomLabel
							error={hasError}
							value={defaultValue && !allEmpty}
							//Having required on customLabel will have double asterisks show up
							disabled={disabled ? true : false}
							htmlFor="outlined-adornment-password"
							name={`${name}-title`}
							shrink={isFormBuilder || undefined}
						>
							<StyledWrapper.LabelContents ref={labelRef}>
								{isFormBuilder && getElementIcon(props.data?.key)}
								{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
								{isFormBuilder && (
									<span>
										{' '}
										{props.data?.unique_identifier
											? `(ff-${props.data?.unique_identifier})`
											: `(tmp-${props.data?.temp_order})`}
									</span>
								)}
							</StyledWrapper.LabelContents>
							{props?.data?.required && <StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>}
						</StyledWrapper.CustomLabel>
					</Tooltip>
					<StyledWrapper.OutlinedInputReadOnly
						id="outlined-adornment-password"
						type={'text'}
						disabled={disabled}
						style={{ width: '100%', color: 'red!important', marginTop: '16px' }}
						inputComponent={CustomInputLink}
						inputProps={{
							hideLinkIcon: props?.data?.custom_options?.includes('hideIcon'),
							chip: props?.data?.chip,
							text: !props?.data?.custom_options?.includes('editOnlyLink')
								? (defaultValue && defaultValue[0]) || ''
								: url,
							link: url,
							onIconClick: () => {
								if (url?.[0] === '/') {
									window.location.href = url;
								} else {
									window.open(url, '_blank');
								}
							}
						}}
						value={url}
						label={parsedLabel?.[0] ? parsedLabel : props?.data?.label}
					/>
				</StyledWrapper.FormControlNoBorder>
			) : (
				<div style={{ display: 'flex', gap: '10px' }}>
					{!props?.data?.custom_options?.includes('editOnlyLink') && (
						<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
							<Tooltip
								arrow
								placement="top"
								title={isOverflowing ? `${props?.data?.label}` : ''}
								enterTouchDelay={0}
							>
								<StyledWrapper.CustomLabel>
									<StyledWrapper.LabelContents ref={labelRef}>
										{isFormBuilder && getElementIcon(props.data?.key)}
										{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
										{isFormBuilder && (
											<span>
												{' '}
												{props.data?.unique_identifier
													? `(ff-${props.data?.unique_identifier})`
													: `(tmp-${props.data?.temp_order})`}
											</span>
										)}
									</StyledWrapper.LabelContents>
									{props?.data?.required && (
										<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
									)}
								</StyledWrapper.CustomLabel>
							</Tooltip>
							<TextField
								error={hasError}
								id="outlined-error-helper-text"
								name={`${name}-title`}
								inputRef={titleInputRef}
								value={titleValue}
								style={styleForNotFormBuilder}
								onChange={handleTitleChange}
								required={props?.data?.required}
								multiline
								InputLabelProps={{ shrink: isFormBuilder || undefined }}
							/>
						</StyledWrapper.FormControlNoBorder>
					)}
					<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
						<Tooltip
							arrow
							placement="top"
							title={isExternalOverflowing ? `${props?.data?.label}` : ''}
						>
							<StyledWrapper.CustomLabel>
								<StyledWrapper.LabelContents ref={labelExternalRef}>
									{isFormBuilder && getElementIcon(props.data?.key)}
									{props.data?.second_label}
									{isFormBuilder && (
										<span>
											{' '}
											{props.data?.unique_identifier
												? `(ff-${props.data?.unique_identifier})`
												: `(tmp-${props.data?.temp_order})`}
										</span>
									)}
								</StyledWrapper.LabelContents>
								{props?.data?.required && (
									<StyledWrapper.AsteriskSpan> *</StyledWrapper.AsteriskSpan>
								)}
							</StyledWrapper.CustomLabel>
						</Tooltip>
						<TextField
							error={hasError}
							id="outlined-error-helper-text"
							name={`${name}-link`}
							inputRef={linkInputRef}
							data-testvalue={'test'}
							value={linkValue}
							onChange={handleLinkChange}
							style={styleForNotFormBuilder}
							required={props?.data?.required}
							multiline
							InputLabelProps={{ shrink: isFormBuilder || undefined }}
						/>
					</StyledWrapper.FormControlNoBorder>
				</div>
			)}
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={hasError}>
					{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
				</FormHelperText>
				{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
			</StyledWrapper.FormHelperTextContainer>
		</>
	);
});

MUILinkInput.displayName = 'MUI_LinkInput';
Registry.register('MUI_LinkInput', MUILinkInput);

export default MUILinkInput;
